import type React from 'react';
import { Button, Modal, Form, Row, Col } from 'react-bootstrap';
import Select from '../../../components/form/AppSelect';
import PaginatedAsyncSelect from '../../../components/form/PaginatedAsyncSelect';
import { useTranslation } from 'react-i18next';

interface CaseStockItemOption {
  value: number | string;
  label: string;
  itemName?: string;
}

export interface CaseUnitOption {
  value: number | string;
  label: string;
}

export type CaseUnitField = 'weight_unit' | 'outer_dimension_unit' | 'inner_dimension_unit';

export type CaseDimensionField =
  | 'outer_length'
  | 'outer_width'
  | 'outer_height'
  | 'inner_length'
  | 'inner_width'
  | 'inner_height';

export interface CaseFormData {
  name: string;
  description: string;
  notes: string;
  weight: string | number;
  weight_unit: CaseUnitOption | null;
  outer_length: string | number;
  outer_width: string | number;
  outer_height: string | number;
  outer_dimension_unit: CaseUnitOption | null;
  inner_length: string | number;
  inner_width: string | number;
  inner_height: string | number;
  inner_dimension_unit: CaseUnitOption | null;
  quantity_total: string | number;
  quantity_available: string | number;
  items_per_case: string | number;
}

type CaseTextField = Exclude<keyof CaseFormData, CaseUnitField>;

const mapUnitOption = (entry: { id: number; symbol: string }): CaseUnitOption => ({
  value: entry.id,
  label: entry.symbol,
});

const DIMENSION_KINDS = ['outer', 'inner'] as const;
const DIMENSION_SIDES = ['length', 'width', 'height'] as const;

interface CaseFormModalProps {
  show: boolean;
  editing: boolean;
  formData: CaseFormData;
  selectedStockItem: CaseStockItemOption | null;
  stockItemOptions: CaseStockItemOption[];
  saving: boolean;
  onClose: () => void;
  onSave: () => void;
  onFieldChange: (field: CaseTextField, value: string) => void;
  onUnitChange: (field: CaseUnitField, option: CaseUnitOption | null) => void;
  onStockItemChange: (option: CaseStockItemOption | null) => void;
  onStockItemSearch: (inputValue: string) => void;
}

const CaseFormModal = ({
  show,
  editing,
  formData,
  selectedStockItem,
  stockItemOptions,
  saving,
  onClose,
  onSave,
  onFieldChange,
  onUnitChange,
  onStockItemChange,
  onStockItemSearch,
}: CaseFormModalProps) => {
  const { t } = useTranslation();

  // The unit only says what the number was entered in — switching it never rewrites the number.
  const renderUnitSelect = (field: CaseUnitField, endpoint: string, ariaLabel: string) => (
    <PaginatedAsyncSelect
      inputId={`case-${field}`}
      aria-label={ariaLabel}
      value={formData[field]}
      onChange={(option: CaseUnitOption | null) => onUnitChange(field, option)}
      endpoint={endpoint}
      mapOption={mapUnitOption}
      isClearable={false}
      placeholder={t('warehouses.cases.unit')}
      noOptionsMessage={() => t('warehouses.products.unitNotFound')}
    />
  );

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {editing ? t('warehouses.cases.editCase') : t('warehouses.cases.newCase')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row className="g-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>{t('warehouses.bundles.stockItem')}</Form.Label>
                <Select
                  options={stockItemOptions}
                  value={selectedStockItem}
                  onChange={(option) => onStockItemChange(option as CaseStockItemOption | null)}
                  onInputChange={onStockItemSearch}
                  isClearable
                  placeholder={t('warehouses.bundles.searchItem')}
                  filterOption={() => true}
                  classNamePrefix="react-select"
                />
              </Form.Group>
            </Col>
            <Col md={8}>
              <Form.Group>
                <Form.Label>{t('warehouses.cases.name')}</Form.Label>
                <Form.Control
                  value={formData.name}
                  onChange={(event) => onFieldChange('name', event.target.value)}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label htmlFor="case-weight">{t('warehouses.cases.emptyWeight')}</Form.Label>
                <Row className="g-2">
                  <Col xs={7}>
                    <Form.Control
                      id="case-weight"
                      type="number"
                      min="0"
                      step="any"
                      value={formData.weight}
                      onChange={(event) => onFieldChange('weight', event.target.value)}
                    />
                  </Col>
                  <Col xs={5}>
                    {renderUnitSelect('weight_unit', 'settings/api/weight-units/', t('warehouses.products.weightUnit'))}
                  </Col>
                </Row>
              </Form.Group>
            </Col>
            {DIMENSION_KINDS.map((kind) => (
              <Col md={12} key={kind}>
                <Form.Group>
                  <Form.Label>{t(`warehouses.cases.${kind}Dimensions`)}</Form.Label>
                  <Row className="g-2">
                    {DIMENSION_SIDES.map((side) => {
                      const field = `${kind}_${side}` as CaseDimensionField;
                      return (
                        <Col xs={6} md={3} key={side}>
                          <Form.Control
                            id={`case-${field}`}
                            type="number"
                            min="0"
                            step="any"
                            value={formData[field]}
                            onChange={(event) => onFieldChange(field, event.target.value)}
                            placeholder={t(`warehouses.cases.${side}`)}
                            aria-label={`${t(`warehouses.cases.${kind}Dimensions`)} ${t(`warehouses.cases.${side}`)}`}
                          />
                        </Col>
                      );
                    })}
                    <Col xs={6} md={3}>
                      {renderUnitSelect(
                        `${kind}_dimension_unit` as CaseUnitField,
                        'settings/api/dimension-units/',
                        `${t(`warehouses.cases.${kind}Dimensions`)} ${t('warehouses.cases.unit')}`,
                      )}
                    </Col>
                  </Row>
                </Form.Group>
              </Col>
            ))}
            <Col md={6}>
              <Form.Group>
                <Form.Label>{t('warehouses.quantityTotal')}</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={formData.quantity_total}
                  onChange={(event) => onFieldChange('quantity_total', event.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>{t('warehouses.quantityAvailable')}</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={formData.quantity_available}
                  onChange={(event) => onFieldChange('quantity_available', event.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>{t('warehouses.bundles.itemsPerCase')}</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={formData.items_per_case}
                  onChange={(event) => onFieldChange('items_per_case', event.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>{t('warehouses.cases.description')}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={formData.description}
                  onChange={(event) => onFieldChange('description', event.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>{t('warehouses.bundles.notes')}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={formData.notes}
                  onChange={(event) => onFieldChange('notes', event.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={onSave} disabled={saving || !formData.name.trim() || !selectedStockItem}>
          {saving ? t('common.saving') : t('common.save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CaseFormModal;
